import { useList, dayName } from "@/lib/api";
import ResourcePage from "@/components/ResourcePage";

const scheduleLabel = (s) => `${s.student_name} — ${s.course_name} (${dayName(s.day_of_week)} ${s.start_time})`;

export function AttendancePage() {
  const { data: schedules } = useList("/schedules");
  return (
    <ResourcePage
      testId="attendance-page"
      title="Attendance"
      description="Student attendance and teacher check-in. Punctuality is computed from check-in time vs class start."
      endpoint="/attendance"
      createLabel="Mark attendance"
      searchKeys={["student_name", "teacher_name", "course_name"]}
      columns={[
        { key: "lesson_date", label: "Date" },
        { key: "student_name", label: "Student", render: (r) => <span className="font-medium">{r.student_name}</span> },
        { key: "course_name", label: "Course" },
        { key: "teacher_name", label: "Teacher" },
        { key: "student_status", label: "Student", badge: true },
        { key: "teacher_check_in_time", label: "Check-in", render: (r) => <span className="font-mono text-xs">{r.teacher_check_in_time || "—"}</span> },
        { key: "punctuality_status", label: "Punctuality", badge: true },
        { key: "is_rescheduled", label: "Rescheduled", render: (r) => r.is_rescheduled ? (
          <span className="text-xs font-mono" title={`Requested by ${r.rescheduled_by_role || "?"}`}>
            → {r.final_date} {r.final_start_time}
          </span>
        ) : "—" },
      ]}
      fields={[
        { name: "class_schedule_id", label: "Class", type: "select", required: true,
          options: (schedules || []).map((s) => ({ value: s.id, label: scheduleLabel(s) })) },
        { name: "lesson_date", label: "Lesson date", type: "date", required: true },
        { name: "student_status", label: "Student status", type: "select", required: true, options: [
          { value: "present", label: "Present" }, { value: "absent", label: "Absent" },
          { value: "late", label: "Late" }, { value: "excused", label: "Excused" }] },
        { name: "teacher_check_in_time", label: "Teacher check-in time", type: "time" },
        { name: "notes", label: "Notes", type: "textarea" },
      ]}
      canEdit={false}
    />
  );
}

export function RemarksPage() {
  const { data: schedules } = useList("/schedules");
  return (
    <ResourcePage
      testId="remarks-page"
      title="Lesson Remarks"
      description="What was covered, homework and media links — visible to parents."
      endpoint="/lesson-remarks"
      createLabel="Add remark"
      searchKeys={["student_name", "teacher_name", "topic_covered"]}
      columns={[
        { key: "lesson_date", label: "Date" },
        { key: "student_name", label: "Student", render: (r) => <span className="font-medium">{r.student_name}</span> },
        { key: "course_name", label: "Course" },
        { key: "topic_covered", label: "Topic", render: (r) => <span className="text-sm line-clamp-2 max-w-xs">{r.topic_covered}</span> },
        { key: "homework", label: "Homework", render: (r) => <span className="text-sm line-clamp-2 max-w-xs">{r.homework}</span> },
        { key: "media_url", label: "Media", render: (r) => r.media_url ? (
          <a href={r.media_url} target="_blank" rel="noreferrer" className="text-secondary underline text-sm" onClick={(e) => e.stopPropagation()}>Link</a>
        ) : "—" },
        { key: "parent_comment", label: "Parent reply", render: (r) => r.parent_comment ? <span className="text-sm line-clamp-2 max-w-xs">{r.parent_comment}</span> : "—" },
      ]}
      fields={[
        { name: "class_schedule_id", label: "Class", type: "select", required: true,
          options: (schedules || []).map((s) => ({ value: s.id, label: scheduleLabel(s) })) },
        { name: "lesson_date", label: "Lesson date", type: "date", required: true },
        { name: "topic_covered", label: "Topic covered", type: "textarea", required: true },
        { name: "homework", label: "Homework", type: "textarea" },
        { name: "teacher_comment", label: "Teacher comment", type: "textarea" },
        { name: "media_url", label: "Media / video link", placeholder: "https://..." },
      ]}
      canEdit={false}
    />
  );
}
